import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { classNames } from 'primereact/utils';
import { calculateCupAmounts, calculateRecipeFromCupAmounts } from '../recipeCalculations';

const formatGrams = (grams) => `${grams.toFixed(1)} g`;

/**
 * Ingredients list with add/remove, including percentage sum and validation.
 */
const IngredientsSection = ({
	formik,
	allFlavors,
	percentageSum,
	areFlavorIdsUnique,
	cupConfigurations = [],
	cupConfigurationsLoading = false,
}) => {
	const { smoothieParts, waterRatio, weightRatio } = formik.values;
	const percentagesComplete = Number(percentageSum) === 100;
	const [draftCupAmounts, setDraftCupAmounts] = useState({});
	const cupAmounts = useMemo(
		() =>
			cupConfigurations
				.map((configuration) => ({
					configuration,
					amounts: calculateCupAmounts(smoothieParts, configuration.cupSize, waterRatio, weightRatio),
				}))
				.filter(({ amounts }) => amounts !== null),
		[cupConfigurations, smoothieParts, waterRatio, weightRatio],
	);

	useEffect(() => {
		setDraftCupAmounts(() => {
			return cupAmounts.reduce((updatedDrafts, { configuration, amounts }) => {
				updatedDrafts[configuration.id] = {
					ingredientGrams: [...amounts.ingredientGrams],
					amountOfWater: amounts.amountOfWater,
					dirty: false,
				};

				return updatedDrafts;
			}, {});
		});
	}, [cupAmounts]);

	const getFlavorName = (part, index) => {
		const flavor = allFlavors.find((option) => option.value === part.flavorId);
		return flavor ? flavor.text : `Ingredient ${index + 1}`;
	};

	const getDraftAmounts = (configuration, amounts) => {
		return (
			draftCupAmounts[configuration.id] || {
				ingredientGrams: amounts.ingredientGrams,
				amountOfWater: amounts.amountOfWater,
				dirty: false,
			}
		);
	};

	const getTotalIngredientGrams = (amounts) => {
		return amounts.ingredientGrams.reduce((sum, grams) => sum + (grams == null ? 0 : Number(grams)), 0);
	};

	const canCalculateDraft = (configuration, amounts) => {
		const hasValidIngredients =
			amounts.ingredientGrams.length > 0 &&
			amounts.ingredientGrams.every((grams) => grams != null && Number.isFinite(Number(grams)) && Number(grams) >= 0);
		const hasValidWater =
			amounts.amountOfWater != null &&
			Number.isFinite(Number(amounts.amountOfWater)) &&
			Number(amounts.amountOfWater) >= 0;

		return (
			hasValidIngredients &&
			hasValidWater &&
			getTotalIngredientGrams(amounts) > 0 &&
			Number(configuration.cupSize) > 0
		);
	};

	const calculateRecipeFromDraft = (configuration, amounts) => {
		const updatedRecipe = calculateRecipeFromCupAmounts(
			amounts.ingredientGrams,
			amounts.amountOfWater,
			configuration.cupSize,
		);
		if (!updatedRecipe) {
			return;
		}

		setDraftCupAmounts((currentDrafts) => {
			return Object.keys(currentDrafts).reduce((updatedDrafts, configurationId) => {
				updatedDrafts[configurationId] = {
					...currentDrafts[configurationId],
					dirty: false,
				};
				return updatedDrafts;
			}, {});
		});

		formik.setValues(
			{
				...formik.values,
				smoothieParts: smoothieParts.map((part, index) => ({
					...part,
					percentage: updatedRecipe.percentages[index],
				})),
				waterRatio: updatedRecipe.waterRatio,
				weightRatio: updatedRecipe.weightRatio,
			},
			true,
		);
	};

	const updateIngredientGrams = (configuration, amounts, ingredientIndex, grams) => {
		const ingredientGrams = [...amounts.ingredientGrams];
		ingredientGrams[ingredientIndex] = grams;

		setDraftCupAmounts((currentDrafts) => ({
			...currentDrafts,
			[configuration.id]: {
				ingredientGrams,
				amountOfWater: amounts.amountOfWater,
				dirty: true,
			},
		}));
	};

	const updateWaterGrams = (configuration, amounts, grams) => {
		setDraftCupAmounts((currentDrafts) => ({
			...currentDrafts,
			[configuration.id]: {
				ingredientGrams: [...amounts.ingredientGrams],
				amountOfWater: grams,
				dirty: true,
			},
		}));
	};

	return (
		<>
			<div className="mt-4 mb-3">
				<h2 className="my-3 text-900" style={{ fontWeight: 600 }}>
					Ingredients
				</h2>
			</div>
			<div className="p-field p-col-12 p-md-6 mt-3">
				<div className="flex align-items-center justify-content-between">
					<div>
						<label className="mr-3 flex flex-column" htmlFor="smoothieParts">
							<span>Ingredients (Flavor and Percentage)</span>
							{formik.values.category.includes('SMOOTHIE') && (
								<small className="block text-blue-500 flex align-items-center">
									<span className="mr-1 flex align-items-center justify-content-center">
										<i className="pi pi-info-circle mr-1" />
									</span>
									<span>For smoothie recipe try to limit the percentage of each ingredient to 50%</span>
								</small>
							)}
						</label>

						{!areFlavorIdsUnique && (
							<small className="p-error">
								<i className="pi pi-exclamation-triangle mr-1" />
								Cannot have duplicate flavors
							</small>
						)}
					</div>

					{formik.values.smoothieParts.length > 0 && (
						<small id="sum-help" className="float-right">
							<span className={`${percentageSum === 100 ? 'text-green-600' : 'p-error'}`}>{percentageSum}/100 %</span>
							{formik.touched.smoothieParts && (
								<span className={`float-right ${percentageSum !== 100 ? 'p-error' : 'p-info'}`}>
									{percentageSum !== 100 && <span> - The sum of percentages must be 100</span>}
								</span>
							)}
						</small>
					)}
				</div>

				{smoothieParts.map((part, index) => (
					<div key={index} className={`card ${formik.values.smoothieParts.length > 1 ? 'mt-2' : ''}`}>
						<div className="flex gap-3 align-items-center">
							<div className={`p-inputgroup ${formik.values.smoothieParts.length > 1 ? 'flex-2' : 'flex-1'}`}>
								<Dropdown
									className={classNames({
										'p-invalid':
											formik.touched.smoothieParts &&
											formik.touched.smoothieParts[index] &&
											formik.errors.smoothieParts &&
											formik.errors.smoothieParts[index] &&
											formik.errors.smoothieParts[index].flavorId,
									})}
									value={part.flavorId}
									options={allFlavors}
									onChange={(e) => {
										const updatedSmoothieParts = formik.values.smoothieParts.map((sp, partIndex) =>
											partIndex === index ? { ...sp, flavorId: e.value } : sp,
										);
										formik.setFieldValue('smoothieParts', updatedSmoothieParts, true);
									}}
									onBlur={formik.handleBlur}
									placeholder="Select a flavor"
									optionLabel="text"
									id="flavorId"
								/>
							</div>

							<div className={`p-inputgroup ${formik.values.smoothieParts.length > 1 ? 'flex-2' : 'flex-1'}`}>
								<InputText
									id="percentage"
									name="percentage"
									placeholder="Enter percentage"
									type="number"
									onChange={(e) => {
										const updatedSmoothieParts = formik.values.smoothieParts.map((sp, partIndex) =>
											partIndex === index ? { ...sp, percentage: e.target.value } : sp,
										);
										formik.setFieldValue('smoothieParts', updatedSmoothieParts, true);
									}}
									onBlur={formik.handleBlur}
									value={part.percentage}
									className={classNames({
										'p-invalid':
											formik.touched.smoothieParts &&
											formik.touched.smoothieParts[index] &&
											formik.errors.smoothieParts &&
											formik.errors.smoothieParts[index] &&
											formik.errors.smoothieParts[index].percentage,
									})}
								/>
								<span className="p-inputgroup-addon">%</span>
							</div>

							{formik.values.smoothieParts.length > 1 && (
								<div className="p-inputgroup flex-1">
									<Button
										type="button"
										icon="pi pi-trash"
										className="p-button-danger p-button-rounded"
										onClick={() => {
											const updatedSmoothieParts = formik.values.smoothieParts.filter(
												(_, partIndex) => partIndex !== index,
											);
											formik.setFieldValue('smoothieParts', updatedSmoothieParts);
										}}
									/>
								</div>
							)}
						</div>

						{formik.touched.smoothieParts &&
							formik.touched.smoothieParts[index] &&
							formik.touched.smoothieParts[index].flavorId &&
							formik.errors.smoothieParts &&
							formik.errors.smoothieParts[index] &&
							formik.errors.smoothieParts[index].flavorId && (
								<small id={`flavorId-${index}-help`} className="p-error block mt-1">
									<i className="pi pi-exclamation-triangle mr-1" />
									{formik.errors.smoothieParts[index].flavorId}
								</small>
							)}

						{formik.touched.smoothieParts &&
							formik.touched.smoothieParts[index] &&
							formik.touched.smoothieParts[index].percentage &&
							formik.errors.smoothieParts &&
							formik.errors.smoothieParts[index] &&
							formik.errors.smoothieParts[index].percentage && (
								<small id={`percentage-${index}-help`} className="p-error">
									<i className="pi pi-exclamation-triangle mr-1" />
									{formik.errors.smoothieParts[index].percentage}
								</small>
							)}

						{formik.values.smoothieParts.length - 1 === index && formik.values.smoothieParts.length < 5 && (
							<div className="flex justify-content-end">
								<Button
									type="button"
									icon="pi pi-plus"
									label="Add"
									className="mt-2 w-fit p-button-secondary"
									onClick={() => {
										formik.setFieldValue('smoothieParts', [
											...formik.values.smoothieParts,
											{ flavorId: '', percentage: 0 },
										]);
									}}
								/>
							</div>
						)}
					</div>
				))}

				<div className="recipe-amounts mt-4">
					<h3 className="mt-0 mb-2 text-900">Amounts per cup size</h3>
					<small className="block mb-3 text-600">
						Enter grams in the cup size used to test the recipe, then select Calculate in that column.
					</small>

					{!percentagesComplete && (
						<Message
							severity="warn"
							text="The ingredient percentages must total 100%. The displayed amounts are provisional."
							className="w-full justify-content-start"
						/>
					)}

					{cupConfigurationsLoading && (
						<Message
							severity="info"
							text="Loading cup configurations..."
							className="w-full justify-content-start"
						/>
					)}

					{!cupConfigurationsLoading && cupConfigurations.length === 0 && (
						<Message
							severity="warn"
							text="No cup configurations are available."
							className="w-full justify-content-start"
						/>
					)}

					{!cupConfigurationsLoading && cupConfigurations.length > 0 && cupAmounts.length === 0 && (
						<Message
							severity="warn"
							text="Enter valid ingredient percentages, water ratio, and weight ratio to calculate amounts."
							className="w-full justify-content-start"
						/>
					)}

					{cupAmounts.length > 0 && (
						<div className="recipe-amounts-table-wrapper border-1 surface-border border-round">
							<table className="recipe-amounts-table" aria-label="Ingredient amounts per cup size">
								<thead>
									<tr>
										<th scope="col">Ingredient</th>
										{cupAmounts.map(({ configuration, amounts }) => {
											const draftAmounts = getDraftAmounts(configuration, amounts);

											return (
												<th key={configuration.id} scope="col">
													<span className="block">{configuration.name}</span>
													<small className="block text-600">{configuration.cupSize} g</small>
													<Button
														type="button"
														label="Calculate"
														icon="pi pi-calculator"
														className="p-button-sm p-button-outlined mt-2 recipe-amounts-calculate"
														disabled={!canCalculateDraft(configuration, draftAmounts)}
														onClick={() => calculateRecipeFromDraft(configuration, draftAmounts)}
													/>
												</th>
											);
										})}
									</tr>
								</thead>
								<tbody>
									{smoothieParts.map((part, ingredientIndex) => (
										<tr key={`${part.flavorId || 'ingredient'}-${ingredientIndex}`}>
											<th scope="row">{getFlavorName(part, ingredientIndex)}</th>
											{cupAmounts.map(({ configuration, amounts }) => {
												const draftAmounts = getDraftAmounts(configuration, amounts);

												return (
													<td key={configuration.id}>
														<InputNumber
															inputId={`ingredient-grams-${configuration.id}-${ingredientIndex}`}
															value={draftAmounts.ingredientGrams[ingredientIndex]}
															onValueChange={(event) =>
																updateIngredientGrams(configuration, draftAmounts, ingredientIndex, event.value)
															}
															mode="decimal"
															min={0}
															minFractionDigits={1}
															maxFractionDigits={2}
															useGrouping={false}
															suffix=" g"
															className="recipe-amounts-input"
															aria-label={`${getFlavorName(part, ingredientIndex)} grams for ${configuration.name}`}
														/>
													</td>
												);
											})}
										</tr>
									))}
									<tr className="recipe-amounts-total-row">
										<th scope="row">Total ingredients</th>
										{cupAmounts.map(({ configuration, amounts }) => {
											const draftAmounts = getDraftAmounts(configuration, amounts);

											return (
												<td key={configuration.id}>{formatGrams(getTotalIngredientGrams(draftAmounts))}</td>
											);
										})}
									</tr>
									<tr className="recipe-amounts-water-row">
										<th scope="row">Water</th>
										{cupAmounts.map(({ configuration, amounts }) => {
											const draftAmounts = getDraftAmounts(configuration, amounts);

											return (
												<td key={configuration.id}>
													<InputNumber
														inputId={`water-grams-${configuration.id}`}
														value={draftAmounts.amountOfWater}
														onValueChange={(event) => updateWaterGrams(configuration, draftAmounts, event.value)}
														mode="decimal"
														min={0}
														minFractionDigits={1}
														maxFractionDigits={2}
														useGrouping={false}
														suffix=" g"
														className="recipe-amounts-input"
														aria-label={`Water grams for ${configuration.name}`}
													/>
												</td>
											);
										})}
									</tr>
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default IngredientsSection;
